import type { TFunction } from "i18next";
import React, { useRef, useMemo, useEffect } from "react";
import type { RefObject } from "react";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputText from "../../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputMask from "../../../../../u2000Core/components/formControls/input/U2000InputMask";
import U2000AutoCompleteInput from "../../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000ComboBox from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import type { U3121ProjectContactHandlerReturn } from "./U3121ProjectContactHandlers";
import { useTranslation } from "react-i18next";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U3120ContactPhoneType, type U3121ProjectContact, type U3121ProjectContactWithSource } from "../../../../U3121Service";
import type { U2000AutoCompleteSearchResult } from "../../../../../u2000Core/types/U2000AutoCompleteSearchResult";

interface U3121ProjectContactEditDlgProps {
  u3121ProjectContactHandler: U3121ProjectContactHandlerReturn;
  t: TFunction;
  NS: string;
  onSearchContactType: (criterias: { fieldName: string; value: unknown }[]) => Promise<Record<string, unknown>[] | U2000AutoCompleteSearchResult<Record<string, unknown>>>;
}

function getPhoneTypeOptions(t: TFunction, NS: string) {
  return [
    { value: U3120ContactPhoneType.OFFICE, label: t("lblPhoneTypeOffice", { ns: NS }) },
    { value: U3120ContactPhoneType.HOME, label: t("lblPhoneTypeHome", { ns: NS }) },
    { value: U3120ContactPhoneType.CELLPHONE, label: t("lblPhoneTypeCellphone", { ns: NS }) },
  ];
}

function U3121ProjectContactEditDlg({ u3121ProjectContactHandler, t, NS, onSearchContactType }: U3121ProjectContactEditDlgProps) {
  const { formStateHandlers } = u3121ProjectContactHandler;
  const { i18n } = useTranslation();
  const contactNameRef = useRef<HTMLInputElement>(null);
  const phoneTypeOptions = useMemo(() => getPhoneTypeOptions(t, NS), [t, NS]);
  const contactData = formStateHandlers.getData();
  const phone1Type = contactData?.phone1Type;
  const phone2Type = contactData?.phone2Type;

  useEffect(() => {
    const data = formStateHandlers.getData();
    if (!data) return;
    const next: U3121ProjectContact = { ...data };
    let updated = false;
    if (data.phone1Type !== U3120ContactPhoneType.OFFICE && data.phone1Extension != null && data.phone1Extension !== "") {
      next.phone1Extension = null;
      updated = true;
    }
    if (data.phone2Type !== U3120ContactPhoneType.OFFICE && data.phone2Extension != null && data.phone2Extension !== "") {
      next.phone2Extension = null;
      updated = true;
    }
    if (updated) formStateHandlers.setData(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clear extensions only when type changes
  }, [phone1Type, phone2Type]);

  const contactTypeResultFields = useMemo(() => {
    const lang = i18n.language;
    if (lang === "fr") return ["contactTypeNameFr"];
    if (lang === "en") return ["contactTypeNameEn"];
    return ["contactTypeName"];
  }, [i18n.language]);

  const displayContactType = (item: Record<string, unknown> | null) => {
    if (!item) return "";
    const lang = i18n.language;
    if (lang === "fr" && item.contactTypeNameFr) return String(item.contactTypeNameFr);
    if (lang === "en" && item.contactTypeNameEn) return String(item.contactTypeNameEn);
    return String(item.contactTypeCode ?? "");
  };

  const extractContactTypeData = (result: Record<string, unknown> | null) => {
    if (result) {
      return [
        { fieldName: "contactType.id", value: result.id },
        { fieldName: "contactType.contactTypeCode", value: result.contactTypeCode ?? "" },
        { fieldName: "contactType.contactTypeNameFr", value: result.contactTypeNameFr ?? "" },
        { fieldName: "contactType.contactTypeNameEn", value: result.contactTypeNameEn ?? "" },
      ];
    }
    return [
      { fieldName: "contactType.id", value: null },
      { fieldName: "contactType.contactTypeCode", value: null },
      { fieldName: "contactType.contactTypeNameFr", value: null },
      { fieldName: "contactType.contactTypeNameEn", value: null },
    ];
  };

  const handleClose = (action: "save" | "cancel") => {
    const isReadMode = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
    if (isReadMode) {
      u3121ProjectContactHandler.onDlgClose("cancel");
      return;
    }
    if (action === "cancel") {
      const originalOnBlur = formStateHandlers.onBlur;
      formStateHandlers.onBlur = () => {};
      u3121ProjectContactHandler.onDlgClose("cancel");
      setTimeout(() => {
        formStateHandlers.onBlur = originalOnBlur;
      }, 0);
    } else {
      u3121ProjectContactHandler.onDlgClose("save");
    }
  };

  const isReadMode = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const isSourceCustomer = (contactData as U3121ProjectContactWithSource)?.source === "customer";
  const readOnly = isReadMode || isSourceCustomer;

  return (
    <U2000SaveCancelDialog
      isOpen={u3121ProjectContactHandler.isDialogOpen}
      onClose={handleClose}
      title={t("lblProjectContact", { ns: NS })}
      isValid={true}
      readOnly={readOnly}
      defaultFocusRef={contactNameRef as RefObject<HTMLElement>}
    >
      <div className="flex flex-col gap-y-4">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <U2000InputText
              fieldName="contactName"
              label={t("lblContactName", { ns: NS })}
              formStateHandlers={formStateHandlers}
              inputRef={contactNameRef as React.RefObject<HTMLInputElement>}
              required={true}
              readonly={readOnly}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <U2000InputText fieldName="title" label={t("lblTitle", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readOnly} />
          </div>
        </div>
        <div>
          <U2000AutoCompleteInput
            formStateHandlers={formStateHandlers}
            fieldName="contactType"
            mainFieldName="contactTypeCode"
            label={t("lblContactType", { ns: NS })}
            onSearch={onSearchContactType}
            displayFn={displayContactType}
            overrideExtractDataFn={extractContactTypeData}
            minCarLaunchSearch={0}
            resultLabels={[]}
            resultFields={contactTypeResultFields}
            required={true}
            readonly={readOnly}
          />
        </div>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-4">
          <div>
            <U2000InputMask fieldName="phone1" label={t("lblPhone1", { ns: NS })} mask="phone" formStateHandlers={formStateHandlers} readonly={readOnly} />
          </div>
          <div>
            <U2000ComboBox
              formStateHandlers={formStateHandlers}
              fieldName="phone1Type"
              label={t("lblPhoneType", { ns: NS })}
              data={phoneTypeOptions}
              keyField="value"
              descField="label"
              getValue={(item) => item.value}
              findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
              readonly={readOnly}
            />
          </div>
          <div className="min-w-[100px]">
            <U2000InputText
              fieldName="phone1Extension"
              label={t("lblPhone1Extension", { ns: NS })}
              formStateHandlers={formStateHandlers}
              readonly={readOnly || phone1Type !== U3120ContactPhoneType.OFFICE}
            />
          </div>
        </div>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-4">
          <div>
            <U2000InputMask fieldName="phone2" label={t("lblPhone2", { ns: NS })} mask="phone" formStateHandlers={formStateHandlers} readonly={readOnly} />
          </div>
          <div>
            <U2000ComboBox
              formStateHandlers={formStateHandlers}
              fieldName="phone2Type"
              label={t("lblPhoneType", { ns: NS })}
              data={phoneTypeOptions}
              keyField="value"
              descField="label"
              getValue={(item) => item.value}
              findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
              readonly={readOnly}
            />
          </div>
          <div className="min-w-[100px]">
            <U2000InputText
              fieldName="phone2Extension"
              label={t("lblPhone2Extension", { ns: NS })}
              formStateHandlers={formStateHandlers}
              readonly={readOnly || phone2Type !== U3120ContactPhoneType.OFFICE}
            />
          </div>
        </div>
        <div>
          <U2000InputText fieldName="email" label={t("lblEmail", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readOnly} />
        </div>
        <div>
          <U2000InputText fieldName="notes" label={t("lblNotes", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readOnly} />
        </div>
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3121ProjectContactEditDlg;
